import React, { useEffect, useState } from 'react';
import CustomLoading from '../../../util_components/custom_loading';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import TpDialog from '../shared/tp_dialog';
import { sort_rows, toggle_sort } from '../shared/use_sortable';
import WhyPopover from './why_popover';
import RuleRefLink from '../rules_engine/rule_ref';
import { fetch_drilldown } from '../api';
import { fmt, is_permission_error } from '../format';
import { collect_session_statuses, session_status_options } from './drilldown_logic';
import { drilldown_view } from './adapters';
import { DRILLDOWN_CATEGORY_ADDITIONAL, drilldown_params } from './params';

/*
 * PROD-7828 R5 — drill-down for one L2 category: item and penalty rows as the backend sends them.
 * Read-only: session edits happen on the Hridaya session details page and the engine recalculates.
 * The backend's show_payment_col hides Payment for trials (base pay is ₹0). The status filter is
 * applied server-side to items AND penalties, each on its own status column. The ADDITIONAL category
 * lists additional-payment entries instead (Q23): their own columns, no session status, amounts signed.
 * Rows are flat (time_label already carries the full IST date, "YYYY-MM-DD HH:MM") and sortable by
 * column header (shared/use_sortable), the same header DataTable gives every other module table.
 */
const EMPTY_VIEW = { days: [], show_payment_col: true, count: 0 };

const SessionDrilldownModal = ({
  show,
  close,
  pay_month,
  month_label,
  teacher_uuid,
  category,
  category_label,
  kind,
  on_error
}) => {
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(EMPTY_VIEW);
  const [statuses, setStatuses] = useState([]);
  const [status_filter, setStatusFilter] = useState('');
  // A failed load is not an empty category: it shows its own error, and attempt drives Try again.
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [sort, setSort] = useState(null);
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));

  // A new category/kind starts unfiltered so the status options cover the whole category.
  useEffect(() => {
    setStatusFilter('');
    setStatuses([]);
    setSort(null);
  }, [teacher_uuid, pay_month, category, kind]);

  useEffect(() => {
    if (!show || !category || !teacher_uuid) return undefined;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetch_drilldown(
      drilldown_params({ teacher_uuid, pay_month, category, kind, session_status: status_filter })
    )
      .then((data) => {
        if (cancelled) return;
        const next = drilldown_view(data);
        setView(next);
        if (!status_filter) setStatuses(collect_session_statuses(next.days));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        if (is_permission_error(e)) on_error(e, 'Failed to load the session drill-down.');
        setView(EMPTY_VIEW);
        setFailed(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, teacher_uuid, pay_month, category, kind, status_filter, attempt, on_error]);

  // The framing and the columns follow the request (known before, and without, a response).
  const entries = category === DRILLDOWN_CATEGORY_ADDITIONAL;
  const show_payment = view.show_payment_col;
  const flat_rows = view.days.flatMap((d) => d.rows);

  const amount_cell = (value, tone, explain) =>
    value === null ? (
      '—'
    ) : (
      <WhyPopover detail={explain} amount_node={<span className={tone}>{fmt(value, 2)}</span>} />
    );

  // R6: a penalty row's own breakdown — original/corrected/waived/free pass from money.* (CD-194: a pass
  // isn't a waiver, so waived never reads waiver_status). An orphan was never charged: money.uncharged_orphan
  // stands in place of the amount, not beside it.
  const penalty_cell = (r) => {
    if (r.is_orphaned) {
      return (
        <WhyPopover
          detail={r.explain}
          amount_node={
            <span className="tp-neg">
              Orphaned - not charged ({fmt(r.uncharged_orphan_amount, 2)})
            </span>
          }
        />
      );
    }
    const has_correction = r.corrected_amount !== null;
    return (
      <WhyPopover
        detail={r.explain}
        amount_node={
          <span>
            <span
              className="tp-neg"
              style={has_correction ? { textDecoration: 'line-through' } : undefined}
            >
              {fmt(r.original_amount, 2)}
            </span>
            {has_correction ? (
              <>
                {' → '}
                <span className="tp-neg">{fmt(r.corrected_amount, 2)}</span>
              </>
            ) : null}
            {r.waived_amount > 0 ? (
              <div className="tp-pos" style={{ fontSize: 10 }}>
                Waived {fmt(r.waived_amount, 2)}
              </div>
            ) : null}
            {r.free_pass_amount > 0 ? (
              <div className="tp-pos" style={{ fontSize: 10 }}>
                Free Pass {fmt(r.free_pass_amount, 2)}
              </div>
            ) : null}
          </span>
        }
      />
    );
  };

  const session_link = (r) =>
    r.session_uuid ? (
      <a
        className="tp-link"
        href={`/sessiondetails?id=${r.session_uuid}`}
        target="_blank"
        rel="noopener noreferrer"
        title={r.session_uuid}
      >
        {r.session_short}
      </a>
    ) : (
      '—'
    );

  const additional_columns = [
    { key: 'date_text', label: 'Date / time (IST)', sort_key: 'date_text', sort_type: 'text' },
    { key: 'category_label', label: 'Category', sort_key: 'category_label', sort_type: 'text' },
    { key: 'source_text', label: 'Source', sort_key: 'source_text', sort_type: 'text' },
    {
      key: 'reference_id',
      label: 'Ref',
      render: (r) => <span className="tp-mono">{r.reference_id || '—'}</span>
    },
    {
      key: 'session',
      label: 'Session',
      render: (r) => <span className="tp-mono">{session_link(r)}</span>
    },
    {
      key: 'amount',
      label: 'Amount',
      align: 'right',
      sort_key: 'amount',
      sort_type: 'number',
      render: (r) => (
        <WhyPopover
          detail={r.explain}
          amount_node={<span className={r.amount_tone}>{r.amount_text}</span>}
        />
      )
    },
    {
      key: 'note',
      label: 'Note',
      render: (r) => (
        <span style={{ fontSize: 11 }} title={r.note || undefined}>
          {r.note || '—'}
        </span>
      )
    },
    {
      key: 'added_by',
      label: 'Added by',
      sort_key: 'added_by',
      sort_type: 'text',
      render: (r) => (
        <span style={{ fontSize: 11 }} title={r.added_by_title || undefined}>
          {r.added_by}
        </span>
      )
    }
  ];

  const item_columns = [
    { key: 'time_label', label: 'Time (IST)', sort_key: 'time_label', sort_type: 'text' },
    {
      key: 'session',
      label: 'Session',
      render: (r) => <span className="tp-mono">{session_link(r)}</span>
    },
    { key: 'status_label', label: 'Status', sort_key: 'status_label', sort_type: 'text' },
    {
      key: 'type_label',
      label: 'Type',
      sort_key: 'type_label',
      sort_type: 'text',
      render: (r) => (
        <span style={{ fontSize: 11 }} title={r.note || undefined}>
          {r.type_label}
          {r.note ? ` — ${r.note}` : ''}
          <RuleRefLink rule_ref={r.rule_ref} on_error={on_error} />
        </span>
      )
    },
    { key: 'duration_label', label: 'Duration' },
    ...(show_payment
      ? [
          {
            key: 'payment',
            label: 'Payment',
            align: 'right',
            sort_key: 'payment',
            sort_type: 'number',
            render: (r) => amount_cell(r.payment, 'tp-pos', r.explain)
          }
        ]
      : []),
    {
      key: 'incentive',
      label: 'Incentive',
      align: 'right',
      sort_key: 'incentive',
      sort_type: 'number',
      render: (r) => amount_cell(r.incentive, 'tp-pos', r.explain)
    },
    {
      key: 'penalty',
      label: 'Penalty',
      align: 'right',
      sort_key: 'penalty',
      sort_type: 'number',
      render: (r) =>
        r.kind === 'penalty' ? penalty_cell(r) : amount_cell(r.penalty, 'tp-neg', r.explain)
    }
  ];

  const columns = entries ? additional_columns : item_columns;
  const rows = sort_rows(flat_rows, columns, sort);

  if (!show) return null;

  return (
    <TpDialog title_id="tp-drilldown-title" on_close={close}>
      <div className="tp-modal tp-modal-wide">
        <h4 id="tp-drilldown-title">
          {entries
            ? `Additional payments · ${month_label}`
            : `Session details — ${category_label || category} · ${month_label}`}
        </h4>
        <div className="tp-ctx">
          {entries
            ? 'Read-only. Entries are added, edited and removed on the Additional payments tab; the payment recalculates from there.'
            : 'Read-only. Session-level corrections are made on the Hridaya session details page; the payment recalculates from there.'}
        </div>

        <div className="tp-frow">
          {entries ? null : (
            <div style={{ width: '220px' }}>
              <CustomSingleSelect
                label="Session status"
                options={session_status_options(statuses)}
                value={status_filter}
                defaultOption="All statuses"
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
          )}
          {failed ? null : <span className="tp-card-hd-sub">{view.count} rows</span>}
        </div>

        {loading ? <CustomLoading /> : null}
        {!loading && failed ? (
          <LoadFailed
            text={entries ? 'Could not load the entries.' : 'Could not load the sessions.'}
            on_retry={() => setAttempt((a) => a + 1)}
          />
        ) : null}
        {!loading && !failed ? (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => r.key}
            sort={sort}
            on_sort={on_sort}
            emptyText={
              entries
                ? 'No additional payments for this teacher and month.'
                : 'No rows for this category and status.'
            }
            cardTitle={(r) => (entries ? r.category_label : r.time_label)}
          />
        ) : null}

        <div className="tp-modal-ft">
          <button type="button" className="tp-btn-ghost" onClick={close}>
            Close
          </button>
        </div>
      </div>
    </TpDialog>
  );
};

export default SessionDrilldownModal;
